import type { QueryClient } from '@tanstack/vue-query'
import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserInfoStore } from '@/stores/user-info.store'
import { useVerifiedUser } from '@/stores/verified-user'
import { usePlacementStore } from '@/stores/placement.store'
import { usePlansStore } from '@/stores/plans.store'
import { useExamResultStore } from '@/stores/exam'
import { useVocabStore } from '@/stores/vocab.store'
import { useChatStore } from '@/stores/chat.store'
import { disconnectSocket } from '@/services/socket'
import { setDeletionResult } from '@/services/deletion-state'
import { storyWrites } from '@/composables/stories/write-queue'

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('account-session') : null

/** Invalidate only the session captured when the operation began. */
export async function clearAccountSession(client: QueryClient, accountId: string, generation: number, broadcast = true) {
  const auth = useAuthStore()
  if (auth.user?.id !== accountId || auth.getSessionGeneration() !== generation) return false
  const failures: unknown[] = []
  const attempt = (action: () => void) => { try { action() } catch (error) { failures.push(error) } }
  // Purge synchronously before any await, so new requests cannot use the old token.
  attempt(() => auth.purgeAuth())
  const clearedGeneration = auth.getSessionGeneration()
  attempt(() => disconnectSocket())
  attempt(() => storyWrites.setAccount(''))
  attempt(() => useUserInfoStore().$reset())
  attempt(() => useVerifiedUser().$reset())
  attempt(() => usePlacementStore().$reset())
  attempt(() => usePlansStore().$reset())
  attempt(() => useChatStore().$reset())
  attempt(() => useExamResultStore().clearExamResult())
  attempt(() => useVocabStore().clearAccountData())
  for (const key of ['auth-store', 'userInfo', 'verified-user', 'placement-store', 'user-plans', 'exam', 'vocabulary']) {
    attempt(() => localStorage.removeItem(key))
  }
  if (broadcast) attempt(() => channel?.postMessage({ type: 'invalidated', accountId }))
  try { await client.cancelQueries() } catch (error) { failures.push(error) }
  if (auth.getSessionGeneration() !== clearedGeneration || auth.isAuthenticated) return false
  attempt(() => client.clear())
  try { if (typeof caches !== 'undefined') await caches.delete('api-cache') } catch (error) { failures.push(error) }
  if (failures.length) console.error('Some account session cleanup steps failed', failures)
  return auth.getSessionGeneration() === clearedGeneration && !auth.isAuthenticated
}

export function listenForSessionInvalidation(client: QueryClient, router: Router) {
  if (!channel) return
  channel.addEventListener('message', async (event) => {
    const auth = useAuthStore()
    const accountId = event.data?.accountId
    if (event.data?.type !== 'invalidated' || !accountId || auth.user?.id !== accountId) return
    if (await clearAccountSession(client, accountId, auth.getSessionGeneration(), false)) {
      setDeletionResult('expired')
      await router.replace({ name: 'account-deletion-result' })
    }
  })
}
